import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  const day = dateObj.getDate().toString().padStart(2, '0')
  const month = dateObj.toLocaleString('en-US', { month: 'short' })
  const year = dateObj.getFullYear()
  return `${day}-${month}-${year}`
}

/**
 * Normalize an API date (ISO datetime or YYYY-MM-DD) for `<input type="date">`.
 * Browsers reject full ISO strings, which leaves the field blank even when DOB exists.
 */
export function toDateInputValue(value?: string | null): string {
  if (!value) return ''
  const trimmed = value.trim()
  if (!trimmed) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed

  const datePart = trimmed.includes('T') ? trimmed.split('T')[0] : trimmed.slice(0, 10)
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart

  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) return ''
  const year = parsed.getUTCFullYear()
  const month = String(parsed.getUTCMonth() + 1).padStart(2, '0')
  const day = String(parsed.getUTCDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function formatDateTime(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(dateObj)
}

export function formatTime(timeString: string): string {
  // Simple formatting for time string HH:MM format
  const [hours, minutes] = timeString.split(':');
  const hour = parseInt(hours);
  const minute = parseInt(minutes);
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour > 12 ? hour - 12 : hour || 12;
  return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
}

export function formatCurrency(amount: number, currency?: string): string {
  // If no currency provided, try the cached tenant currency, then GBP
  const defaultCurrency = currency || (() => {
    try {
      if (typeof window !== 'undefined') {
        const cachedData = localStorage.getItem('tenantCurrency')
        if (cachedData) {
          const { currency: cachedCurrency, timestamp } = JSON.parse(cachedData)
          // Cache for 1 hour
          if (Date.now() - timestamp < 3600000) {
            return cachedCurrency
          }
        }
      }
    } catch (error) {
      console.error('Error reading cached currency in formatCurrency:', error)
    }
    return 'GBP' // Ultimate fallback
  })()

  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: defaultCurrency,
  }).format(amount)
}

export function getCurrencySymbol(currency?: string): string {
  if (!currency) return '£'; // Default to GBP
  switch (currency.toUpperCase()) {
    case 'GBP': return '£';
    case 'USD': return '$';
    case 'EUR': return '€';
    default: return currency; // Return currency code if unknown
  }
}

export function formatPhoneNumber(phone: string): string {
  const cleaned = phone.replace(/\D/g, '')
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`
  }
  return phone
}

export function getInitials(firstName?: string, lastName?: string): string {
  const first = firstName?.charAt(0) || ''
  const last = lastName?.charAt(0) || ''
  return `${first}${last}`.toUpperCase()
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

export function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout
  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

export function generateId(): string {
  return Math.random().toString(36).substring(2, 15) +
         Math.random().toString(36).substring(2, 15)
}

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export function isValidPhone(phone: string): boolean {
  const phoneRegex = /^\+?[\d\s\-\(\)]+$/
  return phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 10
}

export function calculateAge(dateOfBirth: Date): number {
  const today = new Date()
  let age = today.getFullYear() - dateOfBirth.getFullYear()
  const monthDiff = today.getMonth() - dateOfBirth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dateOfBirth.getDate())) {
    age--
  }
  return age
}

export function convert12to24Hour(time12h: string): string {
  // Handle different time formats that might come in
  if (!time12h) return '';

  // If it's already in 24-hour format (HH:MM), return as-is
  if (/^\d{1,2}:\d{2}$/.test(time12h) && !time12h.includes('AM') && !time12h.includes('PM')) {
    const [hours, minutes] = time12h.split(':').map(Number);
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }
  }

  // Convert 12-hour format to 24-hour format
  const time = time12h.trim().toUpperCase();
  const hasAM = time.includes('AM');
  const hasPM = time.includes('PM');

  if (!hasAM && !hasPM) {
    // If no AM/PM indicator, assume it's already 24-hour format
    const [hours, minutes] = time.split(':').map(Number);
    if (!isNaN(hours) && !isNaN(minutes)) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }
    return time12h; // Return original if can't parse
  }

  // Extract the time part (remove AM/PM)
  const timePart = time.replace(/AM|PM/g, '').trim();
  let [hours, minutes] = timePart.split(':').map(Number);

  if (isNaN(hours) || isNaN(minutes)) {
    return time12h; // Return original if can't parse
  }

  // Convert to 24-hour format
  if (hasAM) {
    if (hours === 12) {
      hours = 0; // 12 AM is 00:00 in 24-hour format
    }
  } else if (hasPM) {
    if (hours !== 12) {
      hours += 12; // Add 12 for PM times (except 12 PM)
    }
  }

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

export function getTimeAgo(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  const seconds = Math.floor((new Date().getTime() - dateObj.getTime()) / 1000)

  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`
  const weeks = Math.floor(days / 7)
  if (weeks < 4) return `${weeks} week${weeks > 1 ? 's' : ''} ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`
  const years = Math.floor(days / 365)
  return `${years} year${years > 1 ? 's' : ''} ago`
}